// ABOUTME: The vertical tracker of an application's talent-visible steps: the path with ticks only before the current
// ABOUTME: step, an exit (rejected, withdrawn) only once reached, and the current step's hint (#484, trackerSteps).
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Text } from 'components/Text';
import { TalentApplicationStep, TalentApplicationStepState } from '../types';
import { trackerSteps } from '../utils';

const { DONE, CURRENT } = TalentApplicationStepState;

/**
 * The geometry every row shares. Each marker sits centred in one fixed-width column, so every label starts at the same
 * x whatever the marker's size, and the connector runs through the column's centre. The marker cell is exactly as tall
 * as the first line of a body2 label (22px in the theme), so a marker is centred on that line and a hint under the
 * label grows the row downwards without moving the marker. The lab Timeline this replaced sized its separator column
 * by its dot, which pushed the labels beside the larger check circles further right and put the connector off their
 * centre.
 */
const MARKER_COLUMN = 24;
const LABEL_LINE = 22;
const DONE_SIZE = 20;
const DOT_SIZE = 12;
const CONNECTOR_WIDTH = 2;
/**
 * The gap under every step but the last, in theme spacing units. It is the label column's bottom padding, not the
 * row's: a row's padding would sit outside the stretched marker column, and the connector would stop short of the next
 * marker by exactly that gap.
 */
const ROW_GAP = 1.5;

const List = styled('ol')({
  listStyle: 'none',
  padding: 0,
  margin: 0
});

const Row = styled('li')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  columnGap: theme.spacing(1.5)
}));

/**
 * Stretches to the row's full height, the gap below included (the row itself has no padding), so a connector anchored in
 * it reaches the next row's marker.
 */
const MarkerColumn = styled('div')({
  position: 'relative',
  flex: `0 0 ${MARKER_COLUMN}px`,
  width: MARKER_COLUMN,
  alignSelf: 'stretch'
});

const MarkerCell = styled('div')({
  position: 'relative',
  zIndex: 1,
  height: LABEL_LINE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

/**
 * From this marker's centre to the next marker's centre, behind both: it starts at the first marker and ends at the
 * last, never past them. The hollow marker is filled with the paper colour so the line does not show through it.
 * `data-tone` picks the colour: primary for the segment leaving a DONE step, grey for every other.
 */
const Connector = styled('span')(({ theme }) => ({
  position: 'absolute',
  left: (MARKER_COLUMN - CONNECTOR_WIDTH) / 2,
  width: CONNECTOR_WIDTH,
  top: LABEL_LINE / 2,
  bottom: -LABEL_LINE / 2,
  borderRadius: CONNECTOR_WIDTH,
  backgroundColor: theme.palette.grey[300],
  '&[data-tone="done"]': { backgroundColor: theme.palette.primary.main }
}));

const DoneMarker = styled('span')(({ theme }) => ({
  width: DONE_SIZE,
  height: DONE_SIZE,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText
}));

const CurrentMarker = styled('span')(({ theme }) => ({
  width: DOT_SIZE,
  height: DOT_SIZE,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  // The ring makes the current dot as wide as the check circle (12 + 2 * 4 = 20) without changing its layout box.
  boxShadow: `0 0 0 4px ${theme.palette.primary.light}`
}));

const UpcomingMarker = styled('span')(({ theme }) => ({
  width: DOT_SIZE,
  height: DOT_SIZE,
  borderRadius: '50%',
  boxSizing: 'border-box',
  border: `2px solid ${theme.palette.grey[400]}`,
  backgroundColor: theme.palette.background.paper
}));

/**
 * The marker tells the state at a glance: a filled check for a step already passed, a ringed dot for the step the
 * application is in, a hollow dot for what lies ahead. `data-marker` mirrors it for tests.
 */
const StepMarker = ({ state }: { state: TalentApplicationStepState }) => {
  if (state === DONE) {
    return (
      <DoneMarker data-testid="step-tracker-marker" data-marker={state}>
        <CheckIcon sx={{ fontSize: 14 }} aria-hidden="true" />
      </DoneMarker>
    );
  }
  if (state === CURRENT) {
    return (
      <CurrentMarker data-testid="step-tracker-marker" data-marker={state} />
    );
  }
  return (
    <UpcomingMarker data-testid="step-tracker-marker" data-marker={state} />
  );
};

/**
 * Renders the steps trackerSteps leaves, in the order given. The backend's DONE means "was in this step once"; drawn
 * after the current step after a move back, it read as "already passed" (#484), so trackerSteps keeps a tick only
 * before the current step and leaves the exits out until one is reached.
 */
export const StepTracker = ({
  steps: given
}: {
  steps: TalentApplicationStep[];
}) => {
  const steps = trackerSteps(given);
  if (!steps.length) {
    return null;
  }

  const last = steps.length - 1;

  return (
    <List data-testid="step-tracker">
      {steps.map((step, index) => {
        const isCurrent = step.state === CURRENT;
        return (
          <Row
            key={step.key}
            data-testid="step-tracker-step"
            data-step-key={step.key}
            data-state={step.state}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <MarkerColumn data-testid="step-tracker-marker-column">
              {index < last && (
                <Connector
                  data-testid="step-tracker-connector"
                  data-tone={step.state === DONE ? 'done' : 'upcoming'}
                  aria-hidden="true"
                />
              )}
              <MarkerCell>
                <StepMarker state={step.state} />
              </MarkerCell>
            </MarkerColumn>
            <Box
              data-testid="step-tracker-content"
              sx={{ minWidth: 0, flex: 1, pb: index < last ? ROW_GAP : 0 }}
            >
              <Text
                data-testid="step-tracker-label"
                variant="body2"
                color={isCurrent ? 'text.primary' : 'text.secondary'}
                sx={{
                  fontWeight: isCurrent ? 600 : 400,
                  lineHeight: `${LABEL_LINE}px`
                }}
              >
                {step.label}
              </Text>
              {isCurrent && step.hint && (
                <Text
                  data-testid="step-tracker-hint"
                  variant="caption"
                  color="text.secondary"
                  component="p"
                >
                  {step.hint}
                </Text>
              )}
            </Box>
          </Row>
        );
      })}
    </List>
  );
};
